import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import axios from 'axios';
import Navbar from './Navbar';
import Footer from './Footer';
import { PostMeta } from './PostMeta';
import { MarkdownContent } from './MarkdownContent';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/blog/posts/${slug}`);
        if (mounted) {
          setPost(res.data);
          document.title = `${res.data.title} · Scatura Solutions`;
        }
      } catch (e) {
        if (mounted) {
          const status = e?.response?.status;
          setError(status === 404 || status === 401 || status === 403 ? 'Post not found.' : 'Could not load this post.');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // `API` (module-level constant) never changes; this fetch is meant to re-run only when `slug` changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  return (
    <div className="bg-white text-slate-900 antialiased overflow-x-hidden">
      <Navbar />
      <main className="pt-28 pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[13.5px] text-slate-500 hover:text-slate-900 transition-colors"
            data-testid="blog-back-list"
          >
            <ArrowLeft size={14} /> All posts
          </Link>

          {loading && (
            <div className="mt-10 text-slate-500 text-[14.5px]">Loading post…</div>
          )}
          {!loading && error && (
            <div className="mt-10 text-red-600 text-[14.5px]" data-testid="blog-error">
              {error}
            </div>
          )}
          {!loading && post && (
            <article data-testid="blog-post">
              <PostMeta post={post} />

              {post.cover_image_url && (
                <div className="mt-8 aspect-[16/9] rounded-2xl overflow-hidden bg-slate-100">
                  <img
                    src={post.cover_image_url}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              <MarkdownContent bodyMarkdown={post.body_markdown} />
            </article>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;
